import React, { useEffect, useState } from 'react'
import { PageItem, strippedInputObj } from '../Interfaces/TestCaseInterface';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import { TestList } from './TestList';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import cloneDeep from 'lodash/cloneDeep';
import { TestSuite } from '../Interfaces/TestSuiteInteface';
import { useNavigate, useParams } from 'react-router-dom';

interface TestSuiteEditorProps {
  availableTests: Array<string>,
  setPolicyVariables: React.Dispatch<React.SetStateAction<Record<string, PageItem>>>,
  setPageTemplates: React.Dispatch<React.SetStateAction<Record<string, Array<string>>>>,
  setPageNames: React.Dispatch<React.SetStateAction<Array<string>>>,
  tests: Record<string, Array<Array<strippedInputObj>>>,
  setTests: React.Dispatch<React.SetStateAction<Record<string, Array<Array<strippedInputObj>>>>>,
  setUserJournies: React.Dispatch<React.SetStateAction<Record<string, Array<string>>>>,
  testSuites: Record<string, TestSuite>,
  setTestSuites: React.Dispatch<React.SetStateAction<Record<string, TestSuite>>>,
  setToastContents: React.Dispatch<React.SetStateAction<{ header: string, body: string, severity?: 'error' | 'info' | 'success' | 'warning'  }>>,
  deleteSuite: (deleteSuiteName: string) => void,
  setSuiteName: (oldSuiteName: string, newSuiteName: string, suiteContents: TestSuite) => void
}

export const TestSuiteEditor = (props: TestSuiteEditorProps) => {
  const [suiteName, setSuiteName] = useState<string>("");
  const [environment, setEnvironment] = useState<string>("");
  const [timeOut, setTimeOut] = useState<string>("15");
  const [otpAge, setOtpAge] = useState<string>("15");
  const [testsInSuite, setTestsInSuite] = useState<Array<string>>([]);
  const [editSuite, setEditSuite] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  let params = useParams();
  let navigate = useNavigate();

  useEffect(() => {
    if (params.suite !== undefined && props.testSuites[params.suite] !== undefined) {
      loadSuite();
    }
    else {
      navigate("/test-automation/", { replace: true });
    }
  }, [params]);

  const loadSuite = () => {
    setSuiteName(params.suite!);
    setEnvironment(props.testSuites[params.suite!].TestConfiguration.Environment);
    setTimeOut(props.testSuites[params.suite!].TestConfiguration.TimeOut);
    setOtpAge(props.testSuites[params.suite!].TestConfiguration.OTP_Age);
    setTestsInSuite([...props.testSuites[params.suite!].Tests]);
    setEditSuite(false);
  }

  const saveTestConfig = () => {
    let newSuiteConfig: TestSuite = {
      "TestConfiguration": {
        "Environment": environment,
        "TimeOut": timeOut,
        "OTP_Age": otpAge
      },
      "Tests": testsInSuite
    };
    if (suiteName !== params.suite) {
      props.setSuiteName(params.suite!, suiteName, newSuiteConfig);
    }
    else {
      let newSuites = cloneDeep(props.testSuites);
      newSuites[params.suite!] = newSuiteConfig;
      props.setTestSuites(newSuites);
      props.setToastContents({ header: 'Suite Saved', body: `Saved suite ${params.suite}` });
      setEditSuite(false);
    }
  }

  const downloadJSON = () => {
    let res: TestSuite = {
      "TestConfiguration": {
        "Environment": environment,
        "TimeOut": timeOut,
        "OTP_Age": otpAge
      },
      "Tests": testsInSuite
    };

    let dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(res));
    let downloadAnchorNode = document.createElement('a');
    downloadAnchorNode.setAttribute("href", dataStr);
    downloadAnchorNode.setAttribute("download", params.suite + ".json");
    document.body.appendChild(downloadAnchorNode); // required for firefox
    downloadAnchorNode.click();
    downloadAnchorNode.remove();

    //setOutputJson(JSON.stringify(res, null, 2));
  }



  const toggleToSuite = (test: string) => {
    let newTestsInSuite = [...testsInSuite];

    if (!testsInSuite.includes(test)) {
      newTestsInSuite.push(test);
    } else {
      newTestsInSuite = newTestsInSuite.filter(testSuite => testSuite !== test);
    }

    setTestsInSuite([...newTestsInSuite]);
  }

  const deleteFromSuite = (removedTest: string) => {
    let newTestsInSuite = [...testsInSuite];

    newTestsInSuite = newTestsInSuite.filter(testName => testName !== removedTest);

    setTestsInSuite([...newTestsInSuite]);
  }

  const editTestInSuite = (oldTestName: string, newTestName: string) => {
    let newTestsInSuite = [...testsInSuite];
    let indexToEdit = newTestsInSuite.findIndex(testName => testName === oldTestName);
    newTestsInSuite[indexToEdit] = newTestName;

    setTestsInSuite([...newTestsInSuite]);
  }

  const updateTestOrder = (currentIndex: number, direction: "up" | "down") => {
    let newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    let newTestsInSuite = [...testsInSuite];
    [newTestsInSuite[currentIndex], newTestsInSuite[newIndex]] = [newTestsInSuite[newIndex], newTestsInSuite[currentIndex]];

    setTestsInSuite([...newTestsInSuite]);
  }

  return (
    <Grid container className='mt-4'>
      <Grid item xs ={12} md={8}>
        <Box sx={{display: 'flex', flexDirection:{xs: 'column-reverse', sm: 'row'}}}>
          <Box><Typography variant='h4'>Suite Configuration</Typography></Box>
            <Box className='d-flex same-line'>
              {editSuite ?
                <>
                  <Button onClick={saveTestConfig}>{'Save'}</Button>
                  <Button className='ms-2' color='secondary' onClick={loadSuite}>{'Discard Changes'}</Button>
                </> :
                <>
                  <Button onClick={() => setEditSuite(true)}>{'Edit'}</Button>
                  <Tooltip title='Download Test JSON'><IconButton disabled={props.testSuites[params.suite!] === undefined} onClick={downloadJSON} className='ms-2'><DownloadIcon/></IconButton></Tooltip>
                </>}
                <Tooltip title='Delete Test'><IconButton color='error' className='ms-2' onClick={() => setShowDeleteModal(true)}><DeleteIcon/></IconButton></Tooltip>
            </Box>
        </Box>
      </Grid>
      <Grid item xs={12} className='mt-4 mb-5'>
            <Grid item xs={12} md={6}>
                <TextField
                fullWidth
                  label='Suite Name'
                  disabled={!editSuite}
                  onChange={(e) => setSuiteName(e.target.value)}
                  className='mb-3'
                  value={suiteName} />
                <TextField
                  select
                  fullWidth
                  label='Browser'
                  disabled={!editSuite}
                  value={environment}
                  onChange={(e) => setEnvironment(e.target.value)}
                  className='mb-3'>
                  <MenuItem value="Chrome">Chrome</MenuItem>
                  <MenuItem value="Firefox">Firefox</MenuItem>
                </TextField>
                <TextField
                fullWidth
                  label='Page Load Timeout (seconds)'
                  value={timeOut}
                  disabled={!editSuite}
                  onChange={(e) => setTimeOut(e.target.value)}
                  className='mb-3' />
            </Grid>
            <Grid container>
              <Grid item xs={12} lg={6}>
                <TestList
                  tests={props.tests}
                  toggleToSuite={toggleToSuite}
                  deleteFromSuite={deleteFromSuite}
                  editTestInSuite={editTestInSuite}
                  testsInSuite={testsInSuite}
                  editSuite={editSuite} />
              </Grid>
              <Grid className='mb-4'>
                <label htmlFor="testsInCurrentSuite" className='form-label'><h4>Test Execution Order:</h4></label>
                {testsInSuite.length > 0 ?
                  <List id='testsInCurrentSuite'>
                    {testsInSuite.map((singleTest, index) => {
                      return (<ListItem key={singleTest}>
                        <ListItemText>{singleTest}</ListItemText>
                        <IconButton className='ms-auto orderingButton' disabled={!editSuite || index === 0} color='secondary' size="small" onClick={(e) => updateTestOrder(index, 'up')}><ArrowUpwardIcon/></IconButton>
                        <IconButton className='orderingButton' disabled={!editSuite || index === testsInSuite.length - 1} color='secondary' size="small" onClick={(e) => updateTestOrder(index, 'down')}><ArrowDownwardIcon/></IconButton>
                      </ListItem>)
                    })}
                  </List> :
                  <ul><li className='list-style-none'>No tests assigned to suite</li></ul>}
              </Grid>
            </Grid>
      </Grid>

      <Dialog open={showDeleteModal} onClose={() => setShowDeleteModal(false)}>
        <DialogTitle >
          Confirm Suite Deletion
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete {params.suite}? It cannot be undone.
            </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant='outlined' color="secondary" onClick={() => { setShowDeleteModal(false); }}>
            No
          </Button>
          <Button variant="contained" color='error' onClick={() => { setShowDeleteModal(false); props.deleteSuite(params.suite!); }}>
            Yes I am sure
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  )
}
